import PageTitle from '@/components/PageTitle'
import { Card, CardBody, Col, Row, Spinner, Button, Form, Modal } from 'react-bootstrap'
import { useEffect, useState } from 'react'
import { fetchDocumentosVisor, DocumentoVisor } from '@/servicios/documentacion/visorProvider'
import { fetchPeriodosActivos } from '@/servicios/documentacion/archivosProvider'
import { BiRefresh, BiShow } from 'react-icons/bi'
import { FaSearch } from 'react-icons/fa'
import { useAuthContext } from '@/context/useAuthContext'
import DataTable from 'react-data-table-component'
import { useLayoutContext } from '@/context/useLayoutContext'
import Swal from 'sweetalert2'

// Add CSS for spin animation and PDF security
const securityStyles = `
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.spin {
  animation: spin 1s linear infinite;
}
.pdf-viewer-container {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
}
.pdf-viewer-container * {
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  user-select: none !important;
  -webkit-touch-callout: none !important;
}
.pdf-viewer-iframe {
  pointer-events: auto;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
/* Additional security for PDF elements */
object[type="application/pdf"], 
embed[type="application/pdf"] {
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  user-select: none !important;
  -webkit-touch-callout: none !important;
}
/* Force disable context menu globally when modal is open */
.modal-open .pdf-viewer-container,
.modal-open .pdf-viewer-container * {
  -webkit-context-menu: none !important;
  -moz-context-menu: none !important;
  context-menu: none !important;
}
/* Enhanced right-click prevention */
.pdf-viewer-container object,
.pdf-viewer-container embed {
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  user-select: none !important;
}
/* Enhanced right-click blocking approach */
.pdf-secure {
  position: relative;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  overflow: auto;
}

/* Remove the overlay approach - it blocks scrolling */
/* Allow PDF elements to work normally but prevent context menu */
.pdf-secure object,
.pdf-secure embed {
  pointer-events: auto;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Custom scrollbar for the container */
.pdf-secure::-webkit-scrollbar {
  width: 12px;
}
.pdf-secure::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.pdf-secure::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 6px;
}
.pdf-secure::-webkit-scrollbar-thumb:hover {
  background: #555;
}
`

// Inject styles
if (typeof document !== 'undefined') {
  const style = document.createElement('style')
  style.textContent = securityStyles
  document.head.appendChild(style)
}

const VisorPage = () => {
  const { theme } = useLayoutContext()
  const isDark = theme === 'dark'
  const [documentos, setDocumentos] = useState<DocumentoVisor[]>([])
  const [periodos, setPeriodos] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [isReloading, setIsReloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [idPeriodo, setIdPeriodo] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)
  const { user } = useAuthContext()
  const { token, id_empresa, id } = user || {}
  const [search, setSearch] = useState('')
  const [columnFilters, setColumnFilters] = useState({
    area: '',
    proceso: '',
    documento: '',
  })
  const [showViewerModal, setShowViewerModal] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<DocumentoVisor | null>(null)

  const handleColumnFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setColumnFilters((prev) => ({ ...prev, [name]: value }))
  }

  // Enhanced filtered data with unique keys
  const filteredDocumentos = documentos
    .filter((item) => {
      const s = search.toLowerCase()
      const globalMatch =
        String(item.area).toLowerCase().includes(s) ||
        String(item.proceso).toLowerCase().includes(s) ||
        String(item.documento).toLowerCase().includes(s)

      const columnMatch =
        String(item.area).toLowerCase().includes(columnFilters.area.toLowerCase()) &&
        String(item.proceso).toLowerCase().includes(columnFilters.proceso.toLowerCase()) &&
        String(item.documento).toLowerCase().includes(columnFilters.documento.toLowerCase())

      return globalMatch && columnMatch
    })
    .map((item, index) => ({
      ...item,
      uniqueId: `${item.id_configuracion}-${item.id_area}-${item.id_proceso}-${index}`,
    }))

  const columns = [
    {
      name: 'Área',
      selector: (row: DocumentoVisor) => row.area,
      sortable: true,
      width: '200px',
      cell: (row: DocumentoVisor) => (
        <div
          style={{
            fontWeight: '500',
            color: isDark ? '#f3f4f6' : '#1f2937',
          }}>
          {row.area}
        </div>
      ),
    },
    {
      name: 'Proceso',
      selector: (row: DocumentoVisor) => row.proceso,
      sortable: true,
      width: '200px',
      cell: (row: DocumentoVisor) => (
        <div
          style={{
            fontWeight: '500',
            color: isDark ? '#f3f4f6' : '#1f2937',
          }}>
          {row.proceso}
        </div>
      ),
    },
    {
      name: 'Documento',
      selector: (row: DocumentoVisor) => row.documento,
      sortable: true,
      cell: (row: DocumentoVisor) => (
        <div
          style={{
            fontWeight: '500',
            color: isDark ? '#f3f4f6' : '#1f2937',
          }}>
          {row.documento}
        </div>
      ),
    },
    {
      name: 'Acciones',
      cell: (item: DocumentoVisor) => (
        <div className="d-flex gap-1">
          {item.archivo_final && item.ruta_final && (
            <Button
              size="sm"
              className="d-flex align-items-center"
              onClick={() => handleViewDocument(item)}
              style={{
                backgroundColor: '#17a2b8',
                borderColor: '#17a2b8',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '6px 8px',
                fontSize: '12px',
                fontWeight: '500',
                border: '1px solid #17a2b8',
                whiteSpace: 'nowrap',
                minWidth: 'auto',
              }}>
              <BiShow size={14} className="me-1" />
              Ver documento
            </Button>
          )}
        </div>
      ),
      width: '150px',
      ignoreRowClick: true,
    },
  ]

  useEffect(() => {
    if (token && id_empresa) {
      loadPeriodos()
    }
  }, [token, id_empresa])

  // Add global event listeners to prevent right-click and keyboard shortcuts
  useEffect(() => {
    const handleGlobalContextMenu = (e: MouseEvent) => {
      if (showViewerModal) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (showViewerModal && e.ctrlKey && (e.key === 's' || e.key === 'p' || e.key === 'a')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    if (showViewerModal) {
      document.addEventListener('contextmenu', handleGlobalContextMenu);
      document.addEventListener('keydown', handleGlobalKeyDown);
    }

    return () => {
      document.removeEventListener('contextmenu', handleGlobalContextMenu);
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [showViewerModal])

  const loadPeriodos = async () => {
    setLoading(true)
    try {
      const periodosData = await fetchPeriodosActivos(token, Number(id_empresa))
      setPeriodos(periodosData)
    } catch (error) {
      console.error('Error loading periodos:', error)
      setError('Error al cargar los períodos')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    if (idPeriodo === 0) {
      setError('Por favor seleccione un período')
      return
    }

    setIsReloading(true)
    setError(null)

    try {
      // Get id_usuario from session storage or user context
      const id_usuario = Number(id || sessionStorage.getItem('id_usuario') || '0')

      const documentosData = await fetchDocumentosVisor(idPeriodo, id_usuario, token)
      setDocumentos(documentosData)
      setHasSearched(true)
    } catch (error) {
      console.error('Error loading documentos:', error)
      setError('Error al cargar los documentos')
    } finally {
      setTimeout(() => setIsReloading(false), 500)
    }
  }

  const handleViewDocument = (documento: DocumentoVisor) => {
    setSelectedDocument(documento)
    setShowViewerModal(true)
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    )
  }

  return (
    <>
      <PageTitle title="Visor de Documentos" />

      <Row>
        <Col xs={12}>
          <Card
            className="mb-4"
            style={{
              backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
              border: isDark ? '1px solid #404040' : '1px solid #e5e7eb',
              borderRadius: '12px',
              boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}>
            <CardBody className="p-4">
              <h6
                className="mb-3"
                style={{
                  color: isDark ? '#e5e7eb' : '#374151',
                  fontWeight: '600',
                }}>
                🔍 Filtros de Búsqueda
              </h6>
              <Row className="align-items-end">
                <Col xs={12} md={6} className="mb-3">
                  <Form.Label
                    style={{
                      fontWeight: '500',
                      color: isDark ? '#d1d5db' : '#374151',
                      fontSize: '14px',
                      marginBottom: '8px',
                    }}>
                    📅 Período *
                  </Form.Label>
                  <Form.Select
                    value={idPeriodo}
                    onChange={(e) => setIdPeriodo(Number(e.target.value))}
                    style={{
                      borderRadius: '8px',
                      border: isDark ? '1px solid #404040' : '1px solid #d1d5db',
                      backgroundColor: isDark ? '#374151' : '#ffffff',
                      color: isDark ? '#ffffff' : '#000000',
                      padding: '10px 12px',
                      fontSize: '14px',
                    }}>
                    <option value={0}>Seleccione un período</option>
                    {periodos.map((periodo) => (
                      <option key={periodo.id_periodo} value={periodo.id_periodo}>
                        {periodo.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <Button
                    variant="primary"
                    onClick={handleSearch}
                    disabled={isReloading || idPeriodo === 0}
                    className="w-100 d-flex align-items-center justify-content-center"
                    style={{
                      borderRadius: '8px',
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: 'none',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      color: '#ffffff',
                      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
                      transition: 'all 0.2s ease',
                    }}>
                    <FaSearch className="me-2" />
                    <span>{isReloading ? 'Cargando...' : 'Buscar Documentos'}</span>
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-1">
                <div className="d-flex align-items-center">
                  <h4 className="header-title mb-0 me-2">Visor de Documentos</h4>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleSearch}
                    disabled={isReloading || idPeriodo === 0}
                    className="d-flex align-items-center">
                    <BiRefresh size={16} className={`me-1 ${isReloading ? 'spin' : ''}`} />
                    {isReloading ? 'Cargando...' : 'Recargar'}
                  </Button>
                </div>
              </div>
              <p className="text-muted">Visualización y descarga de documentos con permisos asignados</p>

              {/* Global Search */}
              <div className="mb-4">
                <Form.Control
                  type="text"
                  placeholder="🔍 Buscar en todos los campos..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="border-2"
                  style={{
                    borderRadius: '8px',
                    fontSize: '14px',
                    padding: '10px 15px',
                  }}
                />
              </div>

              {/* Column-specific filters */}
              <Card className="mb-4" style={{ backgroundColor: isDark ? '#2d2d2d' : '#f8f9fa' }}>
                <CardBody className="py-3">
                  <h6 className="mb-3 text-muted">Filtros por Columna</h6>
                  <Row>
                    <Col xs={12} md={4} className="mb-2">
                      <Form.Control
                        type="text"
                        name="area"
                        placeholder="Filtrar por área"
                        value={columnFilters.area}
                        onChange={handleColumnFilterChange}
                        size="sm"
                        style={{ borderRadius: '6px' }}
                      />
                    </Col>
                    <Col xs={12} md={4} className="mb-2">
                      <Form.Control
                        type="text"
                        name="proceso"
                        placeholder="Filtrar por proceso"
                        value={columnFilters.proceso}
                        onChange={handleColumnFilterChange}
                        size="sm"
                        style={{ borderRadius: '6px' }}
                      />
                    </Col>
                    <Col xs={12} md={4} className="mb-2">
                      <Form.Control
                        type="text"
                        name="documento"
                        placeholder="Filtrar por documento"
                        value={columnFilters.documento}
                        onChange={handleColumnFilterChange}
                        size="sm"
                        style={{ borderRadius: '6px' }}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <div
                style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: isDark
                    ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
                    : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  border: isDark ? '1px solid #404040' : '1px solid #e5e7eb',
                }}>
                <DataTable
                  columns={columns}
                  data={filteredDocumentos}
                  keyField="uniqueId"
                  pagination
                  responsive
                  highlightOnHover
                  striped
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[5, 10, 15, 20, 25]}
                  customStyles={{
                    header: {
                      style: {
                        backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                        color: isDark ? '#ffffff' : '#000000',
                        fontSize: '14px',
                        fontWeight: '600',
                        borderBottom: isDark ? '2px solid #404040' : '2px solid #e5e7eb',
                        minHeight: '56px',
                      },
                    },
                    headRow: {
                      style: {
                        backgroundColor: isDark ? '#2d2d2d' : '#f8f9fa',
                        borderTopLeftRadius: '12px',
                        borderTopRightRadius: '12px',
                        borderBottom: isDark ? '1px solid #404040' : '1px solid #dee2e6',
                        minHeight: '52px',
                      },
                    },
                    headCells: {
                      style: {
                        color: isDark ? '#e5e7eb' : '#374151',
                        fontSize: '13px',
                        fontWeight: '600',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      },
                    },
                    rows: {
                      style: {
                        backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                        borderBottom: isDark ? '1px solid #404040' : '1px solid #f1f5f9',
                        color: isDark ? '#ffffff' : '#000000',
                        fontSize: '14px',
                        '&:hover': {
                          backgroundColor: isDark ? '#2d2d2d' : '#f8f9fa',
                          transform: 'scale(1.01)',
                          boxShadow: isDark ? '0 4px 8px rgba(0, 0, 0, 0.3)' : '0 4px 8px rgba(0, 0, 0, 0.1)',
                        },
                        minHeight: '60px',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        transition: 'all 0.2s ease',
                      },
                    },
                    cells: {
                      style: {
                        paddingLeft: '16px',
                        paddingRight: '16px',
                      },
                    },
                    pagination: {
                      style: {
                        backgroundColor: isDark ? '#2d2d2d' : '#f8f9fa',
                        color: isDark ? '#ffffff' : '#000000',
                        borderTop: isDark ? '1px solid #404040' : '1px solid #dee2e6',
                        borderBottomLeftRadius: '12px',
                        borderBottomRightRadius: '12px',
                        minHeight: '56px',
                      },
                    },
                  }}
                  noDataComponent={
                    <div className="text-center py-5">
                      <div style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                        {hasSearched ? (
                          <>
                            <h5>📄 No hay documentos disponibles</h5>
                            <p>No se encontraron documentos con permisos para este período</p>
                          </>
                        ) : (
                          <>
                            <h5>🔍 Seleccione un período</h5>
                            <p>Seleccione un período y haga clic en "Buscar Documentos" para ver los documentos disponibles</p>
                          </>
                        )}
                      </div>
                    </div>
                  }
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Document Viewer Modal */}
      <Modal 
        show={showViewerModal} 
        onHide={() => setShowViewerModal(false)} 
        centered 
        size="xl"
        style={{ zIndex: 9999 }}>
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
            borderBottom: isDark ? '1px solid #404040' : '1px solid #dee2e6',
          }}>
          <Modal.Title style={{ color: isDark ? '#ffffff' : '#000000' }}>
            📄 {selectedDocument?.documento}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
            color: isDark ? '#ffffff' : '#000000',
            padding: '0',
            height: '80vh',
            position: 'relative',
          }}>
          {selectedDocument && selectedDocument.archivo_final && selectedDocument.ruta_final ? (
            <div 
              className="pdf-viewer-container pdf-secure"
              style={{ 
                width: '100%', 
                height: '100%', 
                position: 'relative',
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
              onDragStart={(e) => e.preventDefault()}
            >
              {/* Use object for better PDF viewing with fallback */}
              <object
                data={`https://iso.transexpress.com.gt/documentos${selectedDocument.ruta_final}${selectedDocument.archivo_final}#toolbar=0&navpanes=0&view=FitH&zoom=page-fit`}
                type="application/pdf"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block',
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }}
                onError={() => {
                  console.log('Object element failed to load PDF');
                }}
              >
                {/* Embedded fallback */}
                <embed
                  src={`https://iso.transexpress.com.gt/documentos${selectedDocument.ruta_final}${selectedDocument.archivo_final}#toolbar=0&navpanes=0&view=FitH&zoom=page-fit`}
                  type="application/pdf"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                  }}
                />
                
                {/* Final fallback: Link to open in new tab */}
                <div className="d-flex flex-column justify-content-center align-items-center h-100">
                  <div className="text-center mb-4">
                    <h5 style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                      📄 Vista previa no disponible
                    </h5>
                    <p style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                      El navegador no puede mostrar el PDF directamente.
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => {
                      const pdfUrl = `https://iso.transexpress.com.gt/documentos${selectedDocument.ruta_final}${selectedDocument.archivo_final}`;
                      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
                    }}
                    style={{
                      backgroundColor: '#17a2b8',
                      borderColor: '#17a2b8',
                      padding: '10px 20px',
                    }}>
                    🔗 Abrir en nueva pestaña
                  </Button>
                </div>
              </object>
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
              <div className="text-center">
                <h5 style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                  📄 Documento no disponible
                </h5>
                <p style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                  El archivo del documento no se pudo cargar.
                </p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
            borderTop: isDark ? '1px solid #404040' : '1px solid #dee2e6',
            justifyContent: 'space-between',
          }}>
          <div style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '14px' }}>
            <strong>Área:</strong> {selectedDocument?.area} | 
            <strong> Proceso:</strong> {selectedDocument?.proceso}
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              setShowViewerModal(false)
              setSelectedDocument(null)
            }}
            style={{
              backgroundColor: isDark ? '#6b7280' : '#6c757d',
              borderColor: isDark ? '#6b7280' : '#6c757d',
            }}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default VisorPage
